import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import logo from '@/assets/aksaaf-logo.jpg';

export default function LoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) throw error;
            toast.success('Welcome back');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.message || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row">
            <div className="hidden lg:flex lg:w-1/2 bg-aks-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(201,168,76,0.12),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(201,168,76,0.08),transparent_60%)]" />
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img src={logo} alt="AKSAAF" className="h-9 w-9 object-contain" />
                        </div>
                        <div>
                            <div className="font-bold tracking-tight text-[17px] text-white leading-none">AKSAAF Properties</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">CRM</div>
                        </div>
                    </div>
                    <div>
                        <h1 className="text-5xl font-bold tracking-tight leading-tight mb-4">
                            One pipeline.<br/>
                            <span className="text-aks-gold">Every relationship.</span>
                        </h1>
                        <p className="text-white/70 text-base max-w-md">
                            From first inquiry to repeat investor — manage your entire brokerage on a single, calm, and powerful pipeline.
                        </p>
                        <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
                            {['Bayut', 'Property Finder', 'Meta Ads'].map((s) => (
                                <div key={s} className="px-3 py-2 border border-white/10 rounded-md text-[11px] text-white/70">{s}</div>
                            ))}
                        </div>
                    </div>
                    <div className="text-[11px] text-white/40">© AKSAAF Properties · Single unified pipeline</div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8 flex items-center gap-2">
                        <div className="h-9 w-9 rounded-md border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img src={logo} alt="AKSAAF" className="h-8 w-8 object-contain" />
                        </div>
                        <div>
                            <div className="font-bold tracking-tight text-aks-text leading-none">AKSAAF Properties</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">CRM</div>
                        </div>
                    </div>
                    <h2 className="text-3xl font-bold tracking-tight text-aks-text">Sign in</h2>
                    <p className="text-[14px] text-gray-500 mt-1">Welcome back to AKSAAF CRM</p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="auth-form">
                        <div>
                            <label className="text-[12px] font-medium text-aks-text">Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 focus:border-aks-gold aks-focus-ring outline-none text-[14px]" data-testid="input-email" />
                        </div>
                        <div>
                            <label className="text-[12px] font-medium text-aks-text">Password</label>
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 w-full h-11 px-3 rounded-md border border-gray-300 focus:border-aks-gold aks-focus-ring outline-none text-[14px]" data-testid="input-password" />
                        </div>
                        <button type="submit" disabled={loading} className="w-full h-11 rounded-md bg-aks-navy text-white font-semibold text-[14px] hover:bg-aks-navy-2 disabled:opacity-60 transition aks-focus-ring" data-testid="submit-auth-btn">
                            {loading ? 'Please wait…' : 'Sign in'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
